import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Icon, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import SearchFilter from 'components/SearchFilter';
import { SendFileForm } from 'components/CommonForms';
import withTable from 'HOC/withTable';
import withTableProps from 'HOC/types/withTable';
import { getConfirmationModalConfig, resolveApiErrorMessage, transformTableData } from 'utils';
import { handleCertificateDownload } from 'utils/certificateDownloadHandler';
import { actions as modalActions } from 'redux/modal';
import { actions as reduxTableActions } from 'redux/table';
import { routesAliases } from 'router/routesAliases';
import {
  createTrainingSessionEvidence,
  deleteTrainingSessionCertificate,
  downloadParticipantAssessmentResult,
  downloadTrainingSessionCertificate,
  getTrainingSessionParticipants,
  removeTrainingSessionParticipant,
  setTrainingSessionParticipantFailed,
  uploadTrainingSessionCertificate,
} from 'http/trainingSessions';
import AssignParticipantsModal from './AssignParticipantsModal';
import AttemptsModal from './AttemptsModal';
import { getParticipantsTableConfig } from './helpers/getParticipantsTableConfig';

const pageAlias = 'TrainingSessionParticipants';

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchField: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(2),
    '& > div': {
      marginRight: 0,
    },
  },
  certificateModal: {
    textAlign: 'center',
    '& .MuiDialog-paper': {
      width: '500px',
    },
  },
  certificateWrapper: {
    textAlign: 'center',
  },
  certificateActions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
}));

const isManualEvidenceAllowed = (deliveryMethod, hasMoodleLink) => deliveryMethod !== 'elearning' && !hasMoodleLink;

const isElearningLazyCertificateSession = deliveryMethod => deliveryMethod === 'elearning';

const mapParticipantPerformance = (status, evidenceId) => {
  if (status === 'completed') {
    return true;
  }
  if (status === 'failed') {
    return false;
  }
  if (evidenceId) {
    return true;
  }
  return null;
};

const ParticipantsPanel = ({
  tableData,
  trainingSessionId,
  deliveryMethod,
  hasMoodleLink,
  trainingMethod,
  readOnly = false,
  canAssign = true,
  forTrainer = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('common');
  const [downloadingCertificateId, setDownloadingCertificateId] = useState(null);
  const [downloadingAssessmentId, setDownloadingAssessmentId] = useState(null);
  const manualEvidenceAllowed = isManualEvidenceAllowed(deliveryMethod, hasMoodleLink);
  const isMoodleSession = !manualEvidenceAllowed;
  const isElearningLazyCertificate = isElearningLazyCertificateSession(deliveryMethod);
  const participants = Array.isArray(tableData.data) ? tableData.data : [];

  const refreshParticipants = () => {
    dispatch(reduxTableActions.forceUpdateTable(true));
  };

  const [, removeParticipant] = useAsyncFn(
    async userId => {
      const result = await removeTrainingSessionParticipant(trainingSessionId, userId);
      if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
        dispatch(modalActions.closeModal());
        dispatch(reduxTableActions.forceUpdateTable(true));
      }
      return result;
    },
    [dispatch, trainingSessionId],
  );

  const [passState, markParticipantPassed] = useAsyncFn(
    async userId => {
      const result = await createTrainingSessionEvidence(trainingSessionId, userId);
      if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
        dispatch(modalActions.closeModal());
        dispatch(reduxTableActions.forceUpdateTable(true));
      }
      return result;
    },
    [dispatch, trainingSessionId],
  );

  const [failState, markParticipantFailed] = useAsyncFn(
    async userId => {
      const result = await setTrainingSessionParticipantFailed(trainingSessionId, userId);
      if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
        dispatch(modalActions.closeModal());
        dispatch(reduxTableActions.forceUpdateTable(true));
      }
      return result;
    },
    [dispatch, trainingSessionId],
  );

  const [uploadState, uploadCertificate] = useAsyncFn(
    async (userId, formData) => {
      const result = await uploadTrainingSessionCertificate(trainingSessionId, userId, formData);
      if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
        dispatch(modalActions.closeModal());
        dispatch(reduxTableActions.forceUpdateTable(true));
      }
      return result;
    },
    [dispatch, trainingSessionId],
  );

  const [, downloadCertificate] = useAsyncFn(
    async userId => downloadTrainingSessionCertificate(trainingSessionId, userId),
    [trainingSessionId],
  );

  const [, deleteCertificate] = useAsyncFn(
    async userId => {
      const result = await deleteTrainingSessionCertificate(trainingSessionId, userId);
      if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
        dispatch(modalActions.closeModal());
        dispatch(reduxTableActions.forceUpdateTable(true));
      }
      return result;
    },
    [dispatch, trainingSessionId],
  );

  useEffect(() => {
    tableData.handleChangeFetchMethod(pageAlias, getTrainingSessionParticipants.bind(null, trainingSessionId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingSessionId]);

  const handleTableSearch = (value, searchAlias) => {
    tableData.handleFiltering(searchAlias, value);
  };

  const handleTableSearchClearing = searchAlias => {
    tableData.handleRemoveFilter(searchAlias);
  };

  const handleOpenAttempts = userId => {
    const participant = participants.find(item => item.userId === Number(userId));
    const studentName = participant ? `${participant.firstName || ''} ${participant.lastName || ''}`.trim() : '';

    dispatch(
      modalActions.openModal(
        {
          size: 'large',
          title: t('trainingSessions.attemptsModalTitle', { name: studentName || '—' }),
        },
        <AttemptsModal
          trainingSessionId={trainingSessionId}
          userId={userId}
          studentName={studentName}
          onChanged={refreshParticipants}
          closeModal={() => dispatch(modalActions.closeModal())}
        />,
      ),
    );
  };

  const handleToggleState = (userId, status) => {
    const participant = participants.find(item => item.userId === Number(userId));
    const currentPerformance = mapParticipantPerformance(participant?.status, participant?.evidenceId);

    if (status === 'passed' && currentPerformance === true) {
      return;
    }
    if (status === 'failed' && currentPerformance === false) {
      return;
    }

    let title;
    let message;
    let confirmAction;

    if (status === 'failed') {
      title = t('students.modalFailedTitle');
      message = t('students.modalFailedMessage');
      confirmAction = () => markParticipantFailed(userId);
    } else if (status === 'passed') {
      title = t('trainingSessions.markCompleteTitle');
      message = t('trainingSessions.markCompleteMessage');
      confirmAction = () => markParticipantPassed(userId);
    }

    const [config, element] = getConfirmationModalConfig({
      title,
      confirmAction,
      rejectAction: () => dispatch(modalActions.closeModal()),
      message,
    });
    dispatch(modalActions.openModal(config, element));
  };

  const handleAddCertificate = userId => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingSessions.uploadCertificateTitle'),
        },
        <SendFileForm
          fieldName="certificate"
          onSubmitForm={value => uploadCertificate(userId, value)}
          rejectAction={() => dispatch(modalActions.closeModal())}
          submitting={uploadState.loading}
          fileFormat=".pdf"
        />,
      ),
    );
  };

  const handleDownloadQuizAnswers = async userId => {
    setDownloadingAssessmentId(userId);
    try {
      const result = await downloadParticipantAssessmentResult(trainingSessionId, userId);
      if (result?.error) {
        const [config, element] = getConfirmationModalConfig({
          confirmTitle: t('buttons.ok'),
          confirmAction: () => dispatch(modalActions.closeModal()),
          rejectAction: () => dispatch(modalActions.closeModal()),
          message: resolveApiErrorMessage(result.message, {
            t,
            fallbackMessage: t('errors.serverError'),
          }),
        });
        dispatch(modalActions.openModal(config, element));
      }
    } finally {
      setDownloadingAssessmentId(null);
    }
  };

  const handleShowCertificate = async (userId, rawCertificate) => {
    const participant = participants.find(item => item.userId === userId);
    const certificate =
      rawCertificate || (participant?.certificatePreviewUrl ? { previewUrl: participant.certificatePreviewUrl } : null);

    const previewUrl =
      typeof certificate === 'string'
        ? certificate
        : typeof certificate?.previewUrl === 'string'
        ? certificate.previewUrl
        : '';

    if (certificate && !previewUrl) {
      setDownloadingCertificateId(userId);
      try {
        if (
          await handleCertificateDownload({
            certificate,
            courseId: trainingSessionId,
            userId,
            downloadCertificate: () => downloadCertificate(userId),
            dispatch,
            t,
          })
        ) {
          refreshParticipants();
          return;
        }
      } finally {
        setDownloadingCertificateId(null);
      }
    }

    const certificateUrl = previewUrl || participant?.certificatePreviewUrl || null;

    const picture = certificateUrl ? (
      <img src={certificateUrl} width="200" height="300" alt="" />
    ) : (
      <Icon color="primary" style={{ fontSize: 150 }}>
        image
      </Icon>
    );

    dispatch(
      modalActions.openModal(
        {
          size: 'large',
          title: t('trainingSessions.certificateModalTitle'),
          customStyles: classes.certificateModal,
        },
        <div className={classes.certificateWrapper}>
          {picture}
          <div className={classes.certificateActions}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                downloadCertificate(userId);
                dispatch(modalActions.closeModal());
              }}
            >
              {t('buttons.save')}
            </Button>
            <Button variant="outlined" onClick={() => dispatch(modalActions.closeModal())}>
              {t('buttons.cancel')}
            </Button>
            {!isMoodleSession && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  const [config, element] = getConfirmationModalConfig({
                    title: t('trainingSessions.deleteCertificateTitle'),
                    confirmAction: () => deleteCertificate(userId),
                    rejectAction: () => dispatch(modalActions.closeModal()),
                    message: t('trainingSessions.deleteCertificateMessage'),
                  });
                  dispatch(modalActions.openModal(config, element));
                }}
              >
                {t('trainingSessions.deleteCertificate')}
              </Button>
            )}
          </div>
        </div>,
      ),
    );
  };

  const tableDataExtended = useMemo(
    () =>
      participants.map(item => {
        const isPerformedWithSuccess = mapParticipantPerformance(item.status, item.evidenceId);
        const isFinalized = item.status === 'completed' || item.status === 'failed';
        const certificate =
          item.hasCertificate && item.certificatePreviewUrl
            ? { previewUrl: item.certificatePreviewUrl }
            : item.hasCertificate
            ? {}
            : null;
        const completionDate = item.completionDate || item.evidenceIssuedAt || null;
        const expiryDate = item.evidenceExpiresAt || null;

        return {
          id: item.userId,
          userId: item.userId,
          ids: item.ids || '—',
          fullName: `${item.firstName || ''} ${item.lastName || ''}`.trim() || '—',
          email: item.email || '—',
          completionDate,
          expiryDate,
          isMoodleSession,
          statusParams: {
            userId: item.userId,
            isPerformedWithSuccess,
            isFinalized,
            isFrozen: isFinalized || readOnly,
          },
          attemptParams: {
            userId: item.userId,
            count: item.attemptCount || 0,
            showAttempts: isElearningLazyCertificate,
          },
          evidenceParams: {
            userId: item.userId,
            certificate,
            hasCertificate: Boolean(item.hasCertificate),
            hasAssessmentResult: Boolean(item.hasAssessmentResult),
            isPerformedWithSuccess,
            isMoodleCourse: isElearningLazyCertificate,
            isFrozen: readOnly,
          },
        };
      }),
    [isElearningLazyCertificate, isMoodleSession, participants, readOnly],
  );

  const tableConfig = getParticipantsTableConfig(
    {
      handleToggleState,
      handleAddCertificate,
      handleShowCertificate,
      handleOpenAttempts,
      handleDownloadQuizAnswers,
      isDownloadingCertificate: id => String(downloadingCertificateId) === String(id),
      isDownloadingAssessment: id => String(downloadingAssessmentId) === String(id),
    },
    { showAttemptsColumn: isElearningLazyCertificate && !forTrainer, forTrainer },
  );

  const [columns, tableRows] = useMemo(
    () => transformTableData(tableConfig, tableDataExtended),
    [tableConfig, tableDataExtended],
  );

  const rowActions = useMemo(() => {
    if (forTrainer) {
      return [];
    }

    const actions = [
      {
        type: 'icon',
        icon: 'edit',
        onClick: event => {
          history.push(`${routesAliases.editStudent}/${event.currentTarget.dataset.key}`);
        },
      },
    ];

    if (!readOnly) {
      actions.push({
        type: 'icon',
        icon: 'delete',
        color: 'error',
        onClick: event => {
          const userId = Number(event.currentTarget.dataset.key);
          const [config, element] = getConfirmationModalConfig({
            title: t('trainingSessions.removeParticipantTitle'),
            confirmAction: () => removeParticipant(userId),
            rejectAction: () => dispatch(modalActions.closeModal()),
            message: t('trainingSessions.removeParticipantMessage'),
          });
          dispatch(modalActions.openModal(config, element));
        },
      });
    }

    return actions;
  }, [dispatch, forTrainer, history, readOnly, removeParticipant, t]);

  const openAssignModal = () => {
    dispatch(
      modalActions.openModal(
        { size: 'large', title: t('trainingSessions.assignParticipantsTitle') },
        <AssignParticipantsModal
          trainingSessionId={trainingSessionId}
          onAssigned={refreshParticipants}
          closeModal={() => dispatch(modalActions.closeModal())}
        />,
      ),
    );
  };

  const isLoading = tableData.isFetchingTableData || passState.loading || failState.loading || uploadState.loading;

  return (
    <Box>
      <Box className={classes.toolbar}>
        <Typography variant="h6">{t('trainingSessions.participants')}</Typography>
        {canAssign && !forTrainer ? (
          <Button color="primary" variant="contained" onClick={openAssignModal}>
            {t('trainingSessions.assignParticipants')}
          </Button>
        ) : null}
      </Box>
      <div className={classes.searchField}>
        <SearchFilter
          styles={{
            bgColor: '#e5e5e5',
            searchWidth: '250px',
          }}
          searchAlias="search"
          onSearch={handleTableSearch}
          onClearSearch={handleTableSearchClearing}
        />
      </div>
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={tableRows}
          actions={forTrainer ? undefined : rowActions}
          actionsColumnWidth={forTrainer ? undefined : '100px'}
          totalCount={tableData.totalCount}
          rowsPerPage={tableData.pageSize}
          isLazyLoading
          onChangePage={tableData.handleChangePage}
          onColumnSorting={tableData.handleSorting}
        />
      </ModalBusyContainer>
    </Box>
  );
};

ParticipantsPanel.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
  trainingSessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  deliveryMethod: PropTypes.string,
  hasMoodleLink: PropTypes.bool,
  moodleLmsCourseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trainingMethod: PropTypes.string,
  readOnly: PropTypes.bool,
  canAssign: PropTypes.bool,
  forTrainer: PropTypes.bool,
};

ParticipantsPanel.defaultProps = {
  deliveryMethod: null,
  hasMoodleLink: false,
  moodleLmsCourseId: null,
  trainingMethod: null,
  readOnly: false,
  canAssign: true,
  forTrainer: false,
};

export default withTable({
  defaultSorting: {
    field: 'id',
    dir: 'desc',
  },
  sortingFieldsMapping: {},
  tablePageSize: 5,
  fetchingData: 'items',
  pageAlias,
})(ParticipantsPanel);
